import React, {
    FC,
    useEffect,
    useState,
} from "react"
import {
    Alert,
    Button,
    Card,
    Col,
    Container,
    ListGroup,
    Row,
} from "react-bootstrap"
import axios from "axios"
import {
    useNavigate,
    useParams,
} from "react-router-dom"

import {useAuth} from "../../context/AuthProvider"
import PersonTenantSearch from "./components/PersonTenantSearch"
import {useTranslation} from "react-i18next"
import TenantTree from "./components/TenantTree"
import {
    getTenantLabel,
    TenantTreeNode,
} from "./components/tenant.types"

type ExecutingTenantViewProps =
    Record<string, unknown>

const flattenTree = (
    nodes: TenantTreeNode[]
): TenantTreeNode[] => {
    const result: TenantTreeNode[] = []

    nodes.forEach(node => {
        result.push(node)
        result.push(...flattenTree(node.children))
    })

    return result
}

const ExecutingTenantView: FC<
    ExecutingTenantViewProps
> = () => {
    const {t} = useTranslation("examManagement")
    const auth = useAuth()
    const nav = useNavigate()
    const params = useParams()

    const examId = params.examId

    const mayManage =
        auth.hasAnyPermission([
            "examOfficeEmployee",
            "examOfficeAssistant",
        ])

    const [tree, setTree] =
        useState<TenantTreeNode[]>([])
    const [selectedIds, setSelectedIds] =
        useState<string[]>([])
    const [selectedTenantId, setSelectedTenantId] =
        useState<string | null>(null)
    const [highlightedIds, setHighlightedIds] =
        useState<string[]>([])
    const [loading, setLoading] =
        useState<boolean>(true)
    const [saving, setSaving] =
        useState<boolean>(false)
    const [error, setError] =
        useState<string | null>(null)

    const load = () => {
        if (!examId || !mayManage) {
            setLoading(false)
            return
        }

        setLoading(true)

        Promise.all([
            axios.get(
                "/api/tenant/tree",
                auth.axiosAuthHeader()
            ),
            axios.get(
                `/api/exam/${examId}/executing-tenants`,
                auth.axiosAuthHeader()
            ),
        ])
            .then(([treeResponse, examResponse]) => {
                setTree(
                    Array.isArray(treeResponse.data)
                        ? treeResponse.data
                        : []
                )

                setSelectedIds(
                    Array.isArray(examResponse.data)
                        ? examResponse.data.map(
                            (tenant: TenantTreeNode) =>
                                tenant._id
                        )
                        : []
                )

                setError(null)
            })
            .catch(() => {
                setError(
                    t("executingTenants.loadError")
                )
            })
            .finally(() => {
                setLoading(false)
            })
    }

    useEffect(() => {
        load()
    }, [examId, mayManage])

    const allTenants = flattenTree(tree)

    const selectedTenants = selectedIds
        .map(id => allTenants.find(
            tenant => tenant._id === id
        ))
        .filter(
            (tenant): tenant is TenantTreeNode =>
                Boolean(tenant)
        )

    const addTenant = (
        tenant: TenantTreeNode
    ) => {
        if (selectedIds.includes(tenant._id)) {
            return
        }

        setSelectedIds(previous => [
            ...previous,
            tenant._id,
        ])
    }

    const removeTenant = (
        tenantId: string
    ) => {
        setSelectedIds(previous =>
            previous.filter(id => id !== tenantId)
        )
    }

    const save = () => {
        if (!examId) {
            return
        }

        setSaving(true)

        axios
            .put(
                `/api/exam/${examId}/executing-tenants`,
                {
                    executingTenants: selectedIds,
                },
                auth.axiosAuthHeader()
            )
            .then(() => {
                setError(null)
            })
            .catch(() => {
                setError(
                    t("executingTenants.saveError")
                )
            })
            .finally(() => {
                setSaving(false)
            })
    }

    if (!mayManage) {
        return (
            <Container fluid>
                <Alert variant="danger">
                    {t("executingTenants.forbidden")}
                </Alert>
            </Container>
        )
    }

    return (
        <div className="component">
            <Container fluid>
                <Card>
                    <Card.Header>
                        <div className="d-flex justify-content-between align-items-center gap-2">
                            <h3 className="mb-0">
                                <i className="bi bi-buildings me-2"/>
                                {t("executingTenants.title")}
                            </h3>

                            <Button
                                variant="secondary"
                                onClick={() =>
                                    nav("/exam-management")
                                }
                            >
                                {t("backToOverview")}
                            </Button>
                        </div>
                    </Card.Header>

                    <Card.Body>
                        {error && (
                            <Alert variant="danger">
                                {error}
                            </Alert>
                        )}

                        {loading ? (
                            <Alert variant="info">
                                {t("executingTenants.loading")}
                            </Alert>
                        ) : (
                            <>
                                <PersonTenantSearch
                                    onTenantSelect={tenantId => {
                                        setSelectedTenantId(
                                            tenantId
                                        )
                                    }}
                                    onHighlight={
                                        setHighlightedIds
                                    }
                                />

                                <Row className="g-3">
                                    <Col
                                        xs={12}
                                        xl={8}
                                    >
                                        <Card>
                                            <Card.Header>
                                                {t("tenantTree")}
                                            </Card.Header>

                                            <Card.Body>
                                                <TenantTree
                                                    nodes={tree}
                                                    selectedTenantId={
                                                        selectedTenantId
                                                    }
                                                    highlightedTenantIds={
                                                        highlightedIds
                                                    }
                                                    onSelect={tenant => {
                                                        setSelectedTenantId(
                                                            tenant._id
                                                        )
                                                    }}
                                                    actionLabel={t("add", {ns: "common"})}
                                                    onAction={addTenant}
                                                    actionDisabled={tenant =>
                                                        selectedIds.includes(
                                                            tenant._id
                                                        )
                                                    }
                                                />
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    <Col
                                        xs={12}
                                        xl={4}
                                    >
                                        <Card>
                                            <Card.Header>
                                                {t("executingTenants.assigned")}
                                            </Card.Header>

                                            <ListGroup variant="flush">
                                                {selectedTenants.map(
                                                    tenant => (
                                                        <ListGroup.Item
                                                            key={
                                                                tenant._id
                                                            }
                                                            className="d-flex justify-content-between align-items-center gap-2"
                                                        >
                                                            <span>
                                                                {getTenantLabel(
                                                                    tenant
                                                                )}
                                                            </span>

                                                            <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                onClick={() =>
                                                                    removeTenant(
                                                                        tenant._id
                                                                    )
                                                                }
                                                            >
                                                                {t("remove", {ns: "common"})}
                                                            </Button>
                                                        </ListGroup.Item>
                                                    )
                                                )}

                                                {!selectedTenants.length && (
                                                    <ListGroup.Item>
                                                        {t("executingTenants.empty")}
                                                    </ListGroup.Item>
                                                )}
                                            </ListGroup>
                                        </Card>
                                    </Col>
                                </Row>

                                <div className="mt-3">
                                    <Button
                                        disabled={saving}
                                        onClick={save}
                                    >
                                        <i className="bi bi-save me-1"/>
                                        {t("save", {ns: "common"})}
                                    </Button>
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </div>
    )
}

export default ExecutingTenantView
